/** A stop flagged with power lines, as the Lines on Site warning lists it. */
export type LinesOnSiteStop = { stopNumber: number; siteName: string | null }

/**
 * Order stops of an assignment, numbered the way v1 numbers them: counting
 * order waypoints only, so the yard never shifts "Stop #1" to "Stop #2". This
 * is the only place a stop number is computed; everything else reads it.
 */
export type OrderStop<W extends { order?: unknown }> = {
  waypoint: W
  order: NonNullable<W['order']>
  stopNumber: number
}

export const numberOrderStops = <W extends { order?: unknown }>(
  waypoints: W[],
): OrderStop<W>[] =>
  waypoints
    .filter(
      (wp): wp is W & { order: NonNullable<W['order']> } => wp.order != null,
    )
    .map((wp, index) => ({
      waypoint: wp,
      order: wp.order,
      stopNumber: index + 1,
    }))

type StopLabel = (stopNumber: number) => string

type NumberedOrder<O> = { order: O; stopNumber: number }

/**
 * Markdown for the Instructions block: order instructions only (route-level
 * instructions are deprecated), one `**Stop #N**` heading per order when the
 * route has more than one order (v1).
 */
export const buildAssignmentInstructions = (
  stops: Array<NumberedOrder<{ instructions?: string | null }>>,
  stopLabel: StopLabel,
): string | null => {
  const hasSeveralOrders = stops.length > 1
  const parts = stops.flatMap(({ order, stopNumber }) => {
    if (!order.instructions) return []
    if (!hasSeveralOrders) return [order.instructions]
    return [`**${stopLabel(stopNumber)}**  \n${order.instructions}`]
  })
  return parts.length > 0 ? parts.join('\n\n') : null
}

/** Stops flagged with power lines on site, numbered like the timeline (v1). */
export const selectLinesOnSiteStops = (
  stops: Array<
    NumberedOrder<{
      linesOnSite?: boolean | null
      site?: { name?: string | null } | null
    }>
  >,
): LinesOnSiteStop[] =>
  stops.flatMap(({ order, stopNumber }) => {
    if (!order.linesOnSite) return []
    return [{ stopNumber, siteName: order.site?.name ?? null }]
  })
